import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole } from '../../utils/roleUtils';
import ExtrusionWorkOrderList from './ExtrusionWorkOrderList';
import ViewExtrusionWorkOrder from './ViewExtrusionWorkOrder';
import ResizablePanels from '../../components/Layout/ResizablePanels';
import '../Admin/Users.css';

const ExtrusionsManager = () => {
  const { t } = useTranslation('production');
  const { user } = useAuth();
  const [selectedId, setSelectedId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'view' | 'edit' | 'create'
  const [currentStatus, setCurrentStatus] = useState(null);
  const listRef = useRef();
  const viewRef = useRef();

  const handleSelect = (id) => {
    setSelectedId(id);
    setViewMode('view');
  };

  const handleBack = () => {
    setSelectedId(null);
    setViewMode('list');
    setCurrentStatus(null);
  };

  const handleNew = () => {
    setSelectedId(null);
    setViewMode('create');
    setCurrentStatus(null);
  };

  const handleEdit = () => {
    setViewMode('edit');
  };

  const handleCancel = () => {
    if (viewMode === 'create') {
      setViewMode('list');
      setSelectedId(null);
    } else {
      setViewMode('view');
    }
  };

  const handleSave = () => {
    if (viewRef.current?.handleSave) {
      viewRef.current.handleSave();
    }
  };

  const handleCreated = (id) => {
    setSelectedId(id);
    setViewMode('view');
    if (listRef.current?.refresh) {
      listRef.current.refresh();
    }
  };

  const handleSaveComplete = () => {
    setViewMode('view');
    if (listRef.current?.refresh) {
      listRef.current.refresh();
    }
  };

  const handleStatusLoaded = (status) => {
    setCurrentStatus(status);
  };

  const canCreate = hasRole(user, 'ADMIN', 'MANAGER');
  const canEdit = hasRole(user, 'ADMIN', 'MANAGER') && currentStatus === 'DRAFT';

  const renderToolbar = () => {
    if (viewMode === 'list') {
      return canCreate ? (
        <button className="btn-primary" onClick={handleNew}>
          <span className="icon">+</span> {t('extrusionsManager.newWorkOrder')}
        </button>
      ) : null;
    }
    if (viewMode === 'create' || viewMode === 'edit') {
      return (
        <>
          <button className="toolbar-btn primary" onClick={handleSave}>
            <span className="icon">💾</span> {t('common:ui.save')}
          </button>
          <button className="toolbar-btn" onClick={handleCancel}>
            <span className="icon">✖</span> {t('common:ui.cancel')}
          </button>
        </>
      );
    }
    // View mode
    return (
      <>
        {canEdit && (
          <button className="toolbar-btn" onClick={handleEdit}>
            <span className="icon">✏️</span> {t('common:ui.edit')}
          </button>
        )}
        <button className="toolbar-btn" onClick={handleBack}>
          <span className="icon">⬅️</span> {t('common:ui.back')}
        </button>
      </>
    );
  };

  return (
    <div className="departments-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '0 16px' }}>
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-secondary)', borderRadius: '6px', padding: '2px' }}>
          <span style={{ padding: '6px 16px', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
            🏭 {t('extrusionsManager.title')}
          </span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          {renderToolbar()}
        </div>
      </div>

      <ResizablePanels
        leftPanel={
          <ExtrusionWorkOrderList
            ref={listRef}
            onSelectOrder={handleSelect}
            selectedOrderId={selectedId}
            onNew={handleNew}
          />
        }
        rightPanel={
          viewMode !== 'list' ? (
            <ViewExtrusionWorkOrder
              ref={viewRef}
              orderId={selectedId}
              editMode={viewMode === 'edit'}
              createMode={viewMode === 'create'}
              onBack={handleBack}
              onSave={handleSaveComplete}
              onCancel={handleCancel}
              onOrderCreated={handleCreated}
              onStatusLoaded={handleStatusLoaded}
            />
          ) : null
        }
        leftCollapsed={viewMode !== 'list'}
        defaultLeftWidth={40}
        minLeftWidth={30}
        maxLeftWidth={70}
      />
    </div>
  );
};

export default ExtrusionsManager;
